
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Download, AlertCircle, LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import OrderStatsViewer from '@/components/OrderStatsViewer';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import createTestOrder from '@/utils/createTestOrder';
import { useAdminAuth } from '@/context/AdminAuthContext';

const AdminOrdersPage = () => {
  const navigate = useNavigate();
  const [orderCount, setOrderCount] = useState<number | null>(null);
  const [dbConnectionError, setDbConnectionError] = useState<string | null>(null);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const { adminUser, loginAsAdmin } = useAdminAuth();

  // Check if there are orders in the database on page load
  useEffect(() => {
    const checkOrders = async () => {
      try {
        console.log('Checking if payment_orders table exists and contains data...');
        
        // Check if table exists and has data
        const { count, error } = await supabase
          .from('payment_orders')
          .select('*', { count: 'exact', head: true });
        
        console.log('Order count check result:', { count, error });
        
        if (error) {
          console.error('Error checking orders:', error);
          setDbConnectionError(error.message);
          toast.error('Failed to check order data');
        } else {
          setOrderCount(count || 0);
          if (count === 0) {
            console.log('No orders found in the database');
          } else {
            console.log(`Found ${count} orders in the database`);
            // Try to get one record to verify data can be accessed
            const { data: sampleData, error: sampleError } = await supabase
              .from('payment_orders')
              .select('*')
              .limit(1);
              
            if (sampleError) {
              console.error('Error fetching sample order:', sampleError);
            } else {
              console.log('Sample order data:', sampleData);
            }
          }
        }
      } catch (err) {
        console.error('Exception checking orders:', err);
        setDbConnectionError(err instanceof Error ? err.message : 'Unknown error');
      }
    };
    
    checkOrders();
  }, [adminUser]);

  // Function to export orders as CSV
  const handleExportOrders = async () => {
    try {
      toast.info('Preparing export...');
      
      // Fetch all orders
      const { data, error } = await supabase
        .from('payment_orders')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        throw new Error(`Error fetching orders: ${error.message}`);
      }
      
      if (!data || data.length === 0) {
        toast.info('No orders to export');
        return;
      }
      
      // Convert data to CSV format
      const headers = Object.keys(data[0]).join(',');
      const rows = data.map(order => Object.values(order).map(value => {
        // Handle special cases (like objects, arrays) and escape commas
        if (value === null) return '';
        if (typeof value === 'object') return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
        return `"${String(value).replace(/"/g, '""')}"`;
      }).join(','));
      
      const csv = [headers, ...rows].join('\n');
      
      // Create a blob and download link
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `orders_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Orders exported successfully');
    } catch (err) {
      console.error('Error exporting orders:', err);
      toast.error('Failed to export orders');
    }
  };

  // Function to handle admin login
  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!adminEmail || !adminPassword) {
      toast.error('Please enter both email and password');
      return;
    }
    
    setIsLoggingIn(true);
    
    try {
      console.log('Attempting admin login with:', adminEmail);
      const result = await loginAsAdmin(adminEmail, adminPassword);
      
      if (result) {
        toast.success('Successfully logged in as admin');
        console.log('Admin login successful, user:', result);
        setAdminEmail('');
        setAdminPassword('');
        // Force refresh the current page to ensure all admin components update
        window.location.reload();
      } else {
        toast.error('Invalid admin credentials');
        console.log('Admin login failed - null result returned');
      }
    } catch (err) {
      console.error('Admin login error:', err);
      toast.error('Failed to login. Please check credentials');
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Function to create a test order
  const handleCreateTestOrder = async () => {
    if (isCreatingOrder) return;
    
    setIsCreatingOrder(true);
    toast.info('Creating test order...');
    
    try {
      const result = await createTestOrder();
      
      if (result.success) {
        toast.success('Test order created successfully!');
        
        // Refresh the order count
        const { count } = await supabase
          .from('payment_orders')
          .select('*', { count: 'exact', head: true });
        
        setOrderCount(count || 0);
        
        // Reload the page to refresh the data
        window.location.reload();
      } else if (result.needsAuth) {
        toast.error('Authentication required. Please log in as admin.');
      } else if (result.needsRlsConfig) {
        toast.error('Row-level security policy error. Please configure RLS in Supabase to allow admin users to access the payment_orders table.');
      } else {
        toast.error(`Failed to create test order: ${result.error}`);
      }
    } catch (err) {
      console.error('Error creating test order:', err);
      toast.error('Error creating test order');
    } finally {
      setIsCreatingOrder(false);
    }
  };

  // Check if user is an admin
  const isAdmin = !!adminUser;
  
  console.log('Current admin user state:', adminUser);
  console.log('Is admin:', isAdmin);

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="icon" 
            onClick={() => navigate('/admin')}
            className="mr-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold">Orders Management</h1>
        </div>
        
        <div className="flex gap-2">
          {isAdmin ? (
            <>
              <Button 
                variant="outline" 
                onClick={handleCreateTestOrder}
                className="flex items-center gap-2"
                disabled={isCreatingOrder}
              >
                {isCreatingOrder ? 'Creating...' : 'Create Test Order'}
              </Button>
              
              <Button 
                variant="outline" 
                onClick={handleExportOrders}
                className="flex items-center gap-2"
                disabled={orderCount === 0}
              >
                <Download className="h-4 w-4" />
                Export
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={() => document.getElementById('admin-login-form')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <LogIn className="h-4 w-4" />
              Login
            </Button>
          )}
        </div>
      </div>

      {!isAdmin && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Admin Login</CardTitle>
          </CardHeader>
          <CardContent>
            <form id="admin-login-form" onSubmit={handleAdminLogin} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="admin-email" className="text-sm font-medium">
                    Admin Email
                  </label>
                  <input
                    id="admin-email"
                    type="email"
                    className="w-full px-3 py-2 border rounded-md"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    placeholder="admin@example.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="admin-password" className="text-sm font-medium">
                    Password
                  </label>
                  <input
                    id="admin-password"
                    type="password"
                    className="w-full px-3 py-2 border rounded-md"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>
              <div>
                <Button type="submit" disabled={isLoggingIn} className="w-full md:w-auto">
                  {isLoggingIn ? "Logging in..." : "Login as Admin"}
                </Button>
              </div>
              <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-md border border-gray-200">
                <p className="font-medium mb-1">Admin Credentials:</p>
                <p>Email: sashank@krix.app or siddhartha@krix.app</p>
                <p>Password: Banjara@34</p>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {dbConnectionError && (
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-red-700 mb-2">Database Connection Error</h3>
                <p className="text-red-600 mb-2">There was an error connecting to the payment_orders table:</p>
                <pre className="bg-white p-3 rounded text-sm text-red-800 overflow-auto">{dbConnectionError}</pre>
                <div className="mt-3 text-sm text-red-700">
                  <p><strong>Possible causes:</strong></p>
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    <li>RLS policies restricting access to the table</li>
                    <li>You need to be authenticated with Supabase to access this table</li>
                    <li>The table doesn't exist in the database</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="border-b">
          <CardTitle>Payment Orders</CardTitle>
          {orderCount !== null && (
            <div className="text-sm text-muted-foreground">
              {orderCount > 0 
                ? `${orderCount} order${orderCount === 1 ? '' : 's'} found in database` 
                : 'No orders found in database'}
            </div>
          )}
        </CardHeader>
        <CardContent className="p-6">
          {isAdmin ? (
            <OrderStatsViewer />
          ) : (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium mb-2">Admin Login Required</h3>
              <p className="text-gray-500 mb-4">Please log in as an admin to view and manage orders</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminOrdersPage;
